import type { ClassicEditor } from 'ckeditor5'
import { useCallback } from 'react'

interface CaretPositionFromPointOptions {
    includeUneditableRegion?: boolean
}

type DocumentWithCaretPosition = Document & {
    caretPositionFromPoint?: (
        x: number,
        y: number,
        options?: CaretPositionFromPointOptions
    ) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
}

/**
 * Custom hook for positioning cursor at specific coordinates in CKEditor
 */
export const useCursorPositioning = () => {
    /**
     * Positions the cursor at the specified coordinates within the CKEditor
     * @param editor - The CKEditor instance
     * @param coordinates - The x,y coordinates where to position the cursor
     * @returns boolean - true if positioning was successful, false otherwise
     */
    const positionCursorAtCoordinates = useCallback(
        (
            editor: ClassicEditor,
            coordinates: { x: number; y: number }
        ): boolean => {
            const editableElement = editor.ui?.getEditableElement?.()
            if (!editableElement) return false

            const doc = document as DocumentWithCaretPosition
            let range: Range | null = null

            // Try Firefox method first
            if (doc.caretPositionFromPoint) {
                const caretPosition = doc.caretPositionFromPoint(
                    coordinates.x,
                    coordinates.y
                )
                if (caretPosition && caretPosition.offsetNode) {
                    range = document.createRange()
                    range.setStart(caretPosition.offsetNode, caretPosition.offset)
                    range.collapse(true)
                }
            }
            // Fallback to Chrome/Edge method
            else if (doc.caretRangeFromPoint) {
                range = doc.caretRangeFromPoint(coordinates.x, coordinates.y)
            }

            if (!range) return false

            const viewRange = editor.editing?.view?.domConverter?.domRangeToView?.(
                range
            )
            if (!viewRange) return false

            const modelRange = editor.editing?.mapper?.toModelRange?.(viewRange)
            if (!modelRange) return false

            // Set the cursor position
            editor.model?.change?.((writer) => {
                writer.setSelection(modelRange.start)
            })
            editor.editing?.view?.focus?.()

            return true
        },
        []
    )

    return { positionCursorAtCoordinates }
}